import {
  useListOwners,
  useListPropertyOwners,
  useSetPropertyOwners,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { useCan } from '../../lib/permissions';

interface Share {
  owner_id: string;
  ownership_percent: string;
}

export function OwnershipEditor({ propertyId }: { propertyId: number }) {
  const canManage = useCan('owners.manage');
  const queryClient = useQueryClient();
  const current = useListPropertyOwners(propertyId);
  const owners = useListOwners({ page_size: 200 });
  const save = useSetPropertyOwners();
  const [draft, setDraft] = useState<Share[] | null>(null);

  const saved = okItems(current.data);
  const ownerOptions = okItems(owners.data);

  function submit() {
    if (!draft) return;
    save.mutate(
      {
        propertyId,
        data: {
          owners: draft.map((d) => ({
            owner_id: Number(d.owner_id),
            ownership_percent: d.ownership_percent,
          })),
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          setDraft(null);
        },
      },
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Owners
        </Typography>
        {canManage && !draft && (
          <Button
            variant="outlined"
            onClick={() => {
              save.reset();
              setDraft(
                saved.map((s) => ({
                  owner_id: String(s.owner_id),
                  ownership_percent: String(Number(s.ownership_percent)),
                })),
              );
            }}
          >
            Change ownership
          </Button>
        )}
      </Stack>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {draft ? (
          <Stack spacing={2}>
            {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
            {ownerOptions.length === 0 && (
              <Alert severity="info">Add owners on the Owners page first.</Alert>
            )}
            {draft.map((share, index) => (
              <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <TextField
                  select
                  required
                  size="small"
                  label="Owner"
                  sx={{ flexGrow: 1 }}
                  value={share.owner_id}
                  onChange={(e) =>
                    setDraft(
                      draft.map((d, i) => (i === index ? { ...d, owner_id: e.target.value } : d)),
                    )
                  }
                >
                  {ownerOptions.map((o) => (
                    <MenuItem key={o.id} value={String(o.id)}>
                      {o.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  required
                  size="small"
                  label="Percent"
                  type="number"
                  sx={{ width: 110 }}
                  value={share.ownership_percent}
                  onChange={(e) =>
                    setDraft(
                      draft.map((d, i) =>
                        i === index ? { ...d, ownership_percent: e.target.value } : d,
                      ),
                    )
                  }
                />
                <IconButton
                  aria-label="Remove this owner"
                  onClick={() => setDraft(draft.filter((_, i) => i !== index))}
                >
                  ×
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setDraft([...draft, { owner_id: '', ownership_percent: '' }])}
                disabled={ownerOptions.length === 0}
              >
                Add owner
              </Button>
              <span style={{ flexGrow: 1 }} />
              <Button onClick={() => setDraft(null)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={submit}
                disabled={save.isPending || draft.some((d) => !d.owner_id || !d.ownership_percent)}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        ) : saved.length === 0 ? (
          <Typography color="text.secondary">No owners recorded for this property.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {saved.map((s) => (
              <Typography key={s.owner_id}>
                {s.owner_name}: {Number(s.ownership_percent)}%
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
